import Api from '../Services/Api.js';
import React, { Component } from 'react';
import UserService from '../Services/UserService.js';
import Cookies from 'universal-cookie';
import ability from '../abilities';

const defaultStore = {
	getApi: () => {},
	auth: {
		setUser: () => {},
		getUser: () => {},
		setAccessToken: () => {}
	},
	bootstrapped: false
}

class WebServiceProvider extends Component
{
	state = {
		user: null,
		bootstrapped: false
	};

	useRefreshTokenIfAvailable (headers) {
		const that = this;

		if ('authorization' in headers) {
			const token = headers.authorization.replace('Bearer ', '');
			that.setAccessToken(token);
			that.rememberAccessToken(token);
			return true;
		}

		return false;
	}

	constructor (props) {
		super(props);

		const that = this;

		this.api = new Api("");

		this.api.setGlobalExceptionHandler(
			response => {
				that.useRefreshTokenIfAvailable(response.headers);
				return response;
			},
			error => {
				const shouldRetry = that.useRefreshTokenIfAvailable(error.response.headers);

				if (shouldRetry) {
					error.config.headers.Authorization = error.response.headers.authorization;
				}

				if (! shouldRetry && (error.status === 401 || error.response.data.message === 'Unauthorized')) {
					if (that.getUser()) {
						that.setUser(null);
					}
					return Promise.reject(error);
				} else if (shouldRetry) {
					return Promise.reject(Object.assign(error, { action: 'retry' }));
				} else {
					return Promise.reject(error);
				}
			}
		);
	}

	getUser = () => {
		return this.state.user;
	}

	updateUserProperties = (properties) => {
		this.setState({ user: this.getUser().updateProperties(properties) });
	}

	setUser = (user) => {
		const that = this;
		return new Promise(function (resolve, reject) {
			that.setState({ user: user }, function () { 

				ability.update([]);

				if (!user) 
				{
					this.forgetAccessToken();
					reject();
				} 
				else 
				{					
					let abilities = user.getAbilities();

					if (user.isAdmin()) {
						abilities = Object.assign({ "manage.all": null }, abilities);
					}

					ability.update(that.convertServerTokensToAbilities(abilities));
					that.rememberAccessToken(that.api.getAccessToken());

					resolve(user);
				}
			});
		});
	}

	checkIfCanLogin = () => {
		const that = this;
		return new Promise(
			(resolve, reject) => {
				const canLogin = function () {
					const userService = new UserService(that.getApi());

					return new Promise(
						(yes, no) => {
							userService.me()
								.then(response => {
									const { user, server } = response;

									that.useRefreshTokenIfAvailable(server.headers);

									if (! user) {
										no();
									} else {
										yes(user);
									}
								})
								.catch(() => no());
						}
					);
				};

				// Can the user login with the current saved token?
				canLogin().then((user) => {
					// Attempt to set the user to the current saved token.
					that.setUser(user)
						.then(
							() => resolve()
						).catch(
							() => reject()
						);
				// The user is not able to login, maybe the token needed a refresh.
				}).catch(
					() => canLogin()
						// The token actually needed a refresh, we'll try again.
						.then((user) => {
							that.setUser(user).then(() => resolve());
						})
						// The token did not need a refresh, so we won't log the user in.s
						.catch(
							() => reject()
						)
				);
			}
		);
	}

	setAccessToken = (accessToken) => {
		this.api.setAccessToken(accessToken);
	}
	
	getRemindedAccessToken () {
		const cookies = new Cookies();
		return cookies.get('tovertaalAccessToken');
	}

	rememberAccessToken (accessToken) {
		const cookies = new Cookies();
		cookies.set('tovertaalAccessToken', accessToken, { path: '/' });
	}

	forgetAccessToken () {
		const cookies = new Cookies();
		cookies.remove('tovertaalAccessToken');
	}

	componentDidMount() {
		const that = this;
		const remindedAccessToken = this.getRemindedAccessToken();

		if (remindedAccessToken)
		{
			this.setAccessToken(remindedAccessToken);
			this.checkIfCanLogin()
				.then(() => that.setState({ bootstrapped: true }))
				.catch((response) => {
					that.forgetAccessToken();
					that.setState({ bootstrapped: true });
				});
		} 
		else 
		{
			this.setState({ bootstrapped: true });
		}
	}

	getApi = () => {
		return this.api;
	}

	convertServerTokensToAbilities = (tokens) => {	
		let convertedTokens = [];

		Object.keys(tokens).forEach(function (token) {
			if (tokens[token] === 'manage.all') {
				return {
					actions: 'manage',
					subject: 'all'
				};
			}

			const resourceKey = tokens[token];
			const tokenSplit = token.split('.');
			const action = tokenSplit[0];
			const entity = tokenSplit[1];

			if (resourceKey !== null) {
				const entitySplit = entity.split('::');

				convertedTokens.push({
					actions: action,
					subject: entitySplit[0],
					conditions: {
						key: resourceKey
					}
				});
			}

			convertedTokens.push({
				actions: action,
				subject: entity
			});
		});

		return convertedTokens;
	}

	render () {
		return (
			<WebServiceContext.Provider
				value={{
					getApi: this.getApi,
					auth: {
						setAccessToken: this.setAccessToken.bind(this),
						setUser: this.setUser.bind(this),
						getUser: this.getUser.bind(this),
						updateUserProperties: this.updateUserProperties.bind(this),
						checkIfCanLogin: this.checkIfCanLogin.bind(this)
					},
					bootstrapped: this.state.bootstrapped
				}}
			>
				{this.props.children}
			</WebServiceContext.Provider>
		);
	}
}

const WebServiceContext = React.createContext(defaultStore);

export default WebServiceProvider;
export const WebServiceContextConsumer = WebServiceContext.Consumer;

export function withLoggedUser(Component) {
	return function withLoggedUserComponent (props) {
		return (
			<WebServiceContextConsumer>
			{
				context => 
					<Component 
						{...props} 
						webServiceContext={
							{
								auth: {
									getUser: context.auth.getUser,
									updateUserProperties: context.auth.updateUserProperties
								}
							}
						}
					/>
			}
			</WebServiceContextConsumer>
		);
	}
}

export function withApi(Component) {
	return function withApiComponent(props) {
		return (
			<WebServiceContextConsumer>
			{
				context => 
					<Component 
						{...props} 
						webServiceContext={
							{
								getApi: context.getApi,
								auth: {
									setAccessToken: context.auth.setAccessToken,
									setUser: context.auth.setUser,
									getUser: context.auth.getUser,
									checkIfCanLogin: context.auth.checkIfCanLogin,
									updateUserProperties: context.auth.updateUserProperties
								},
								bootstrapped: context.bootstrapped
							}
						}
					/>
			}
			</WebServiceContextConsumer>
		);
	}
}

export function showIfAuthenticated(Component) {
	return function showIfAuthenticatedComponent(props) {
		return (
			<WebServiceContextConsumer>
			{
				context => {
					if (context.auth.getUser()) {
						return (<Component {...props} />)
					} else {
						return (null);
					}
				}
			}
			</WebServiceContextConsumer>
		);
	}
}


// WEBPACK FOOTER //
// ./src/Providers/WebServiceProvider.js